/* eslint-disable max-lines -- Pre-existing violation */
import {skipToken} from '@reduxjs/toolkit/query';
import {
  Alert,
  Button,
  ButtonGroup,
  Card,
  closeCurrentDialog,
  closeDialog,
  DataStatus,
  DialogFooter,
  DropdownItem,
  Flag,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  Label,
  openDialog,
  openDropdown,
  Segment,
  Separator,
  showNotification,
  TextInput,
  Tooltip,
} from 'platform/components';
import {Box, Grid, GridItem, Hide, HStack, Show, Space, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useEffect, useRef, useState} from 'react';
import {DeepPartial, UseFormReturn} from 'react-hook-form';

import {defaultTo, isNil, isNotNil, not} from 'ramda';
import {isNilOrEmpty, isString} from 'ramda-adjunct';

import {
  useGetManufacturersQuery,
  useGetStorageLocationByIdApiQuery,
  useGetTreeFolderQuery,
} from '@dms/api/core';
import {CustomerResponseBodyV2} from '@dms/api/customer';
import {TenantResponseBody} from '@dms/api/shared';
import {
  GetReceiveNoteItemNewResponse,
  GetReceiveNoteItemResponse,
  GetWarehouseResponse,
  PatchReceiveNoteItemManualRequest,
  PostArticleRequest,
  PostReceiveNoteBasketItemRequest,
  PutSupplierArticlePromptValidationRequest,
  ReceiveNoteState,
  useGetArticleCategoryListQuery,
  useGetDispensingUnitPresetsQuery,
  useGetHandlingUnitsQuery,
  useGetReceiveNoteStateQuery,
  useGetSupplierDiscountGroupsQuery,
  useGetWarehousesQuery,
  useLazyGetArticleIdByManufacturerNumberAndManufacturerIdQuery,
  useLazyGetReceiveNoteItemNewQuery,
  useLazyGetReceiveNoteItemQuery,
  useLazyGetReceiveNoteItemRefreshedQuery,
  usePatchReceiveNoteItemManualMutation,
  usePostArticleMutation,
  usePutSupplierArticlePromptValidationMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {
  DEFAULT_CURRENCY,
  getCustomerName,
  getOptionsFromManufacturers,
  getOptionsFromWarehouses,
  handleApiError,
  logError,
  refreshDmsDatagrid,
  useGetSupplierCustomersQuery,
  usePermissions,
} from '@dms/shared';
import {
  ArticleCreate,
  ArticleCreateDefaultValues,
  ArticleCreateTreeFolderPath,
  ArticleState,
  AssignLocationDialog,
  CreatedArticleData,
  getOptionsFromHandlingUnits,
  handleWarehouseAclApiError,
} from '@dms/shared-aftersales';
import {SUPPLIER_ARTICLE_PROMPT} from '@dms/warehouse/shared';
import {SupplierArticlePrompt} from '@dms/warehouse/shared-components';

import {
  AllOrNone,
  ApiError,
  generateHashFromObjects,
  Nullish,
  RequiredTestIdProps,
  suffixTestId,
  useOnMount,
} from 'shared/web';

import {RECEIVE_NOTE_ITEM_DIALOG, REPLACE_ARTICLE_DIALOG} from '../constants/receiveNoteItemDialog';
import {ItemRowData} from '../types/ItemRowData';
import {ArticleDetailCard} from './ArticleDetailCard';
import {AvailabilitySegment} from './AvailabilitySegment';
import {DeliveryItemDetailCard} from './DeliveryItemDetailCard';
import {DeliveryItemForm} from './DeliveryItemForm';
import {useReceiveNoteItemFormRecalculation} from './hooks/useReceiveNoteItemFormRecalculation';
import {ReceiveNoteItemForm} from './ReceiveNoteItemForm';
import {ReplaceArticleModal} from './ReplaceArticleModal';
import {RequestsSegment} from './RequestsSegment';
import {SupplierArticleForm} from './SupplierArticleForm';
import {SupplierOrdersSegment} from './SupplierOrdersSegment';
import {ReceiveNoteItemDetails} from './types/ReceiveNoteItemDetails';
import {
  ReceiveNoteItemDetailSchema,
  ReceiveNoteItemFormType,
} from './types/ReceiveNoteItemDetailSchema';

// There is a delay on the backend between replacing the article and reflecting this change in the data grid
const WAREHOUSE_AVAILABILITY_REFRESH_DELAY_MS = 1000;

const ASSIGN_LOCATION_DIALOG_ID = 'receive-note-assign-storage-location-dialog';

const getSupplierNameById = (
  suppliers: CustomerResponseBodyV2[] | Nullish,
  supplierId: string | Nullish
) => getCustomerName(suppliers?.find((supplier) => supplier.id === supplierId));

enum Segments {
  AVAILABILITY = 'availability',
  REQUESTS = 'requests',
  SUPPLIER_ORDERS = 'supplierOrders',
}

type Mode = 'creating' | 'editing' | 'adding';

type WarehouseType =
  | GetWarehouseResponse
  | {
      id: string;
      warehouseNumber?: string;
      name: string;
      authorizationProfileId: string;
      defaultManufacturerId?: string;
      currency?: string;
    };

export type ReceiveNoteItemDetailProps = {
  receiveNoteId: string | Nullish;
  authorizationProfileId: string | Nullish;
  receiveNoteSupplierId: string | Nullish;
  itemRowData?: ItemRowData | Nullish;
  warehouse: WarehouseType | Nullish;
  tenant: TenantResponseBody | Nullish;
  state: ReceiveNoteState | Nullish;
  receiveNoteItemId?: string;
  defaultValues?: DefaultValues;
  mode: Mode;
  isFromReceiveNote?: boolean;
  receiveNoteType?: string | Nullish;
  isReplaceArticleAlwaysDisabled?: boolean;
  onClose?: VoidFunction;
} & RequiredTestIdProps &
  BasketProps;

type BasketProps = AllOrNone<{
  context?: 'BASKET';
  onAddToBasket?: (requestBody: PostReceiveNoteBasketItemRequest['body']) => Promise<void>;
  isAddingToBasket?: boolean;
}>;

type DefaultValues = {
  catalogNumber?: string;
  manufacturerId?: string;
  warehouseId?: string;
  name?: string;
  activeFolderId?: string;
};

export type HandleCheckItemConfig = {
  manufacturerId: string;
  catalogNumber: string;
  warehouseId: string;
  supplierId?: string;
};

// eslint-disable-next-line complexity, max-lines-per-function, max-statements -- Pre-existing violation
export function ReceiveNoteItemDetail(props: ReceiveNoteItemDetailProps) {
  const [activeSegment, setActiveSegment] = useState<Segments>(Segments.AVAILABILITY);

  const [canEditReceiveNote] = usePermissions({
    permissionKeys: ['receiveNoteEdit'],
  });

  const [leafId, setLeafId] = useState<string | Nullish>(null);
  const [leafIdErrorMessage, setLeafIdErrorMessage] = useState<string | undefined>(undefined);
  const [lastCheckedState, setLastCheckedState] = useState<HandleCheckItemConfig | null>(null);

  const [articleState, setArticleState] = useState<ArticleState | null>(null);
  const [replaceArticleId, setReplaceArticleId] = useState<string | null>(null);
  const [selectedStorageLocationId, setSelectedStorageLocationId] = useState<string | null>(null);

  const [receiveNoteItem, setReceiveNoteItem] = useState<GetReceiveNoteItemResponse | Nullish>(
    null
  );

  const [receiveNoteItemNew, setReceiveNoteItemNew] = useState<
    GetReceiveNoteItemNewResponse | Nullish
  >(null);

  const [receiveNoteItemDetails, setReceiveNoteItemDetails] = useState<ReceiveNoteItemDetails>();

  const {data: receiveNoteState, isLoading: isReceiveNoteLoading} = useGetReceiveNoteStateQuery(
    {
      receiveNoteId: props.receiveNoteId as string,
    },
    {skip: isNil(props.receiveNoteId)}
  );

  const {data: warehouses, isLoading: isWarehouseLoading} = useGetWarehousesQuery();

  const {data: suppliers, isLoading: areSuppliersLoading} = useGetSupplierCustomersQuery();

  const {data: manufacturers, isLoading: areManufacturersLoading} = useGetManufacturersQuery();

  const {data: handlingUnits, isLoading: isLoadingHandlingUnits} = useGetHandlingUnitsQuery(
    undefined,
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const {data: dispensingUnitPresets, isLoading: isDispensingUnitPresetsLoading} =
    useGetDispensingUnitPresetsQuery();

  const {data: categories} = useGetArticleCategoryListQuery();

  const [getReceiveNoteItem, {isLoading: isReceiveNoteItemLoading}] =
    useLazyGetReceiveNoteItemQuery();

  const [getReceiveNoteItemNew, {isFetching: isNewReceiveNoteItemLoading}] =
    useLazyGetReceiveNoteItemNewQuery();

  const [getReceiveNoteItemRefreshed, {isFetching: isRefreshingDetails}] =
    useLazyGetReceiveNoteItemRefreshedQuery();

  const [getArticleIdByManufacturerNumberAndManufacturerId, {isFetching: isCheckingForArticle}] =
    useLazyGetArticleIdByManufacturerNumberAndManufacturerIdQuery();

  const [putSupplierArticlePromptValidation] = usePutSupplierArticlePromptValidationMutation();

  const [patchReceiveNoteItemManual, {isLoading: isPatchReceiveNoteItemLoading}] =
    usePatchReceiveNoteItemManualMutation({fixedCacheKey: 'edit-receive-note-item'});

  const [createArticle, {isLoading: isCreateArticleLoading}] = usePostArticleMutation();

  const {data: storageLocationDetail} = useGetStorageLocationByIdApiQuery(
    isNotNil(selectedStorageLocationId) ? {storageLocationId: selectedStorageLocationId} : skipToken
  );
  const storageLocationName = storageLocationDetail?.location ?? null;

  const defaultHandlingUnit = handlingUnits?.find((handlingUnit) => handlingUnit.isDefault);

  const articleId = receiveNoteItemNew?.article?.id ?? receiveNoteItem?.article?.id;

  const supplierId =
    receiveNoteItemNew?.supplierArticle?.supplierId ?? receiveNoteItem?.supplierArticle?.supplierId;

  const manufacturerNumber =
    receiveNoteItemNew?.article?.manufacturerNumber ?? receiveNoteItem?.article?.manufacturerNumber;

  const manufacturerId =
    receiveNoteItemNew?.article?.manufacturerId ?? receiveNoteItem?.article?.manufacturerId;

  const deliveryNoteQuantity =
    receiveNoteItemNew?.deliveryNoteItem?.quantity ?? receiveNoteItem?.deliveryNoteItem?.quantity;

  const deliveryNoteUnitPrice =
    receiveNoteItemNew?.deliveryNoteItem?.purchasePrice ??
    receiveNoteItem?.deliveryNoteItem?.purchasePrice;

  const deliveryNoteTotalPrice =
    receiveNoteItemNew?.deliveryNoteItem?.purchaseValue ??
    receiveNoteItem?.deliveryNoteItem?.purchaseValue;

  const warehouseUnitId =
    receiveNoteItemNew?.article?.handlingUnit ?? receiveNoteItem?.article?.handlingUnit;

  const articleHandlingUnitName = handlingUnits?.find(
    (handlingUnit) => handlingUnit.id === warehouseUnitId
  )?.name;

  const warehouseName = warehouses?.find(
    (warehouse) => warehouse?.id === props.warehouse?.id
  )?.name;

  const defaultCurrency = props.tenant?.currency ?? DEFAULT_CURRENCY;
  const currency = defaultTo(defaultCurrency, props.warehouse?.currency);

  const isCreating = props.mode === 'creating';
  const isEditing = props.mode === 'editing';
  const isOpeningBalance = receiveNoteState?.type === 'OPENING_BALANCE';
  const isTransfer =
    receiveNoteState?.type === 'TRANSFER' || receiveNoteState?.type === 'EXTERNAL_TRANSFER';
  const isEditingDisabled = isEditing && props.state === 'COMPLETED';
  const isOpeningBalanceEditingDisabled = isEditing && isOpeningBalance;
  const isTransferEditingDisabled = isEditing && isTransfer;
  const doesExist = articleState === 'doesExist';
  const doesNotExist = articleState === 'doesNotExist';
  const hasBeenCreated = articleState === 'hasBeenCreated';
  const isChecked = doesExist || doesNotExist || hasBeenCreated;
  const isLoadingMainContent = isCheckingForArticle || isNewReceiveNoteItemLoading;
  const isNotChecked = not(articleState);
  const shouldCardsBeExpanded = isChecked || isEditing;
  const defaultCategoryId = categories?.categories?.find((category) => category.isDefault)?.id;
  const isArticleReplacement =
    props.isReplaceArticleAlwaysDisabled !== true &&
    (receiveNoteItemNew?.receiveNoteItem.isArticleReplaceAllowed ??
      receiveNoteItem?.receiveNoteItem.isArticleReplaceAllowed);

  const isArticleReplaced = receiveNoteItem?.deliveryNoteItem.isArticleReplaced;

  const {data: defaultTreeFolder, isLoading: isDefaultTreeFolderLoading} = useGetTreeFolderQuery(
    {contextTarget: 'spare-parts-catalog', contextId: defaultCategoryId ?? ''},
    {skip: isNil(defaultCategoryId)}
  );

  const {data: supplierDiscountGroups, isLoading: isLoadingSupplierDiscountGroups} =
    useGetSupplierDiscountGroupsQuery(
      {
        supplierId: supplierId as string,
        authorizationProfileId: props.authorizationProfileId as string,
      },
      {skip: isNilOrEmpty(supplierId) || isNilOrEmpty(props.authorizationProfileId)}
    );

  const defaultArticleValues = match(props.mode)
    .with('creating', () => ({
      catalogNumber: props.defaultValues?.catalogNumber,
      manufacturerId: props.defaultValues?.manufacturerId,
      warehouseId: props.defaultValues?.warehouseId,
      name: props.defaultValues?.name,
    }))
    // Must come from the fetched item — itemRowData stays frozen on the originally
    // opened row while the row control navigates between items.
    .with('editing', () => ({
      catalogNumber: receiveNoteItem?.article.manufacturerNumber,
      manufacturerId: receiveNoteItem?.article.manufacturerId,
      warehouseId: props.warehouse?.id,
      name: receiveNoteItem?.article.name,
    }))
    .with('adding', () => ({
      catalogNumber: props.itemRowData?.catalogNumber,
      manufacturerId: props.itemRowData?.manufacturerId,
      warehouseId: props.warehouse?.id,
      name: props.itemRowData?.name,
    }))
    .exhaustive();

  const defaultValues: DeepPartial<ReceiveNoteItemFormType> = {
    article: {
      ...defaultArticleValues,
      handlingUnit: warehouseUnitId ?? defaultHandlingUnit?.id,
      supplierId: props.itemRowData?.supplierId, // used only for price list tab flow
    },
    receiveNoteItem: {
      serialNumber: receiveNoteItem?.receiveNoteItem?.serialNumber,
      quantity: defaultTo(1, receiveNoteItem?.receiveNoteItem?.quantity),
      unitPrice: defaultTo(0, receiveNoteItem?.receiveNoteItem?.purchasePrice),
      totalPrice: defaultTo(0, receiveNoteItem?.receiveNoteItem?.purchaseValue),
      vatType: defaultTo('S', receiveNoteItem?.receiveNoteItem?.vatType),
      isUnitSellingPriceEnabled: false,
      unitSellingPrice:
        receiveNoteItem?.article?.dispensingPrices?.saleBasePriceWithoutVat ?? undefined,
    },
    deliveryItem: {
      serialNumber: receiveNoteItem?.deliveryNoteItem?.serialNumber,
      quantityVerified: defaultTo(1, receiveNoteItem?.deliveryNoteItem?.quantityVerified),
      unitPrice: defaultTo(0, receiveNoteItem?.deliveryNoteItem?.purchasePrice),
      totalPrice: defaultTo(0, receiveNoteItem?.deliveryNoteItem?.purchaseValue),
      vatType: defaultTo('S', receiveNoteItem?.deliveryNoteItem?.vatType),
      purchasePriceVerified: defaultTo(0, receiveNoteItem?.deliveryNoteItem?.purchasePriceVerified),
      stockValueVerified: defaultTo(0, receiveNoteItem?.deliveryNoteItem?.stockValueVerified),
    },
    supplierArticle: {
      supplierQuantity: defaultTo(1, receiveNoteItem?.supplierArticle?.supplierQuantity),
      supplierUnitId: receiveNoteItem?.supplierArticle?.supplierUnitId,
      warehouseQuantity: defaultTo(1, receiveNoteItem?.supplierArticle?.warehouseQuantity),
      warehouseUnitId: receiveNoteItem?.article?.handlingUnit,
      supplierOrderingNumber: receiveNoteItem?.supplierArticle?.supplierOrderingNumber,
      supplierBulkPackageQuantity: defaultTo(
        1,
        receiveNoteItem?.supplierArticle?.supplierBulkPackageQuantity
      ),
      supplier: getSupplierNameById(suppliers, receiveNoteItem?.supplierArticle?.supplierId),
      supplierDiscountGroup: receiveNoteItem?.supplierArticle?.supplierDiscountGroupCode,
    },
  };

  const {handleChange, isCalculateLoading, setPrevFormValues} =
    useReceiveNoteItemFormRecalculation();

  const formApiRef = useRef<UseFormReturn<ReceiveNoteItemFormType> | null>(null);

  const shouldCloseAfterSaveRef = useRef(true);

  useOnMount(() => {
    if (props.mode === 'editing') {
      handleGetReceiveNoteItemEdit();
      return;
    }

    if (
      props.mode === 'adding' &&
      isString(props.itemRowData?.manufacturerId) &&
      isString(props.itemRowData?.catalogNumber) &&
      isString(props.warehouse?.id)
    ) {
      handleCheckItem({
        manufacturerId: props.itemRowData?.manufacturerId,
        catalogNumber: props.itemRowData?.catalogNumber,
        warehouseId: props.warehouse?.id,
        supplierId: props.receiveNoteSupplierId ?? undefined,
      });
      return;
    }

    if (
      props.mode === 'creating' &&
      isString(props.defaultValues?.manufacturerId) &&
      isString(props.defaultValues?.catalogNumber) &&
      isString(props.defaultValues?.warehouseId)
    ) {
      handleCheckItem({
        manufacturerId: props.defaultValues?.manufacturerId,
        catalogNumber: props.defaultValues?.catalogNumber,
        warehouseId: props.defaultValues?.warehouseId,
        supplierId: props.receiveNoteSupplierId ?? undefined,
      });
      return;
    }
  });

  useEffect(
    function setDefaultLeafId() {
      if (isNotNil(leafId) || isNil(defaultTreeFolder)) {
        return;
      }

      setLeafId(defaultTreeFolder?.treeFolder?.id);
    },
    [defaultTreeFolder, leafId]
  );

  useEffect(
    function fetchReplacedItem() {
      if (isNil(replaceArticleId)) {
        return;
      }

      handleGetReceiveNoteItemReplaced(replaceArticleId);
    },
    // eslint-disable-next-line react/exhaustive-deps
    [replaceArticleId]
  );

  const handleGetReceiveNoteItemEdit = async () =>
    await getReceiveNoteItem({
      receiveNoteId: props.receiveNoteId as string,
      itemId: props.receiveNoteItemId as string,
    })
      .unwrap()
      .then((receiveNoteItem) => {
        // Set the prevFormValues now, we need them for proper recalculation of prices
        handleMapPrevFormValues(receiveNoteItem);
        setReceiveNoteItem(receiveNoteItem);
        setReceiveNoteItemDetails({
          deliveryItemDetail: {
            manufacturerNumber: receiveNoteItem?.deliveryNoteItem?.manufacturerNumber,
            manufacturerId: receiveNoteItem?.deliveryNoteItem?.manufacturerId,
            supplierNumber: receiveNoteItem?.deliveryNoteItem?.supplierNumber,
            name: receiveNoteItem?.deliveryNoteItem?.name,
            description: receiveNoteItem?.deliveryNoteItem?.description,
            note: receiveNoteItem?.deliveryNoteItem?.note,
            location: receiveNoteItem?.deliveryNoteItem?.location,
            vin: receiveNoteItem?.deliveryNoteItem?.vin,
            keyCode: receiveNoteItem?.deliveryNoteItem?.keyCode,
            engineNumber: receiveNoteItem?.deliveryNoteItem?.engineNumber,
            engineCode: receiveNoteItem?.deliveryNoteItem?.engineCode,
          },
          articleDetail: receiveNoteItem.article,
        });
      })
      .then(() => setArticleState('doesExist'))
      .catch(handleApiError);

  const handleGetReceiveNoteItem = async (articleId: string, wasCreated: boolean) => {
    if (isNilOrEmpty(props.receiveNoteId)) {
      return;
    }

    const request = {
      receiveNoteId: props.receiveNoteId as string,
      articleId,
    };

    await getReceiveNoteItemNew(request)
      .unwrap()
      .then((result) => {
        handleArticleState(result, wasCreated);
        resetFormValues(result);
        setReceiveNoteItemDetails({
          deliveryItemDetail: {
            manufacturerNumber: result?.deliveryNoteItem?.manufacturerNumber,
            manufacturerId: result?.deliveryNoteItem?.manufacturerId,
            supplierNumber: result?.deliveryNoteItem?.supplierNumber,
            name: result?.deliveryNoteItem?.name,
            description: result?.deliveryNoteItem?.description,
          },
          articleDetail: result.article,
        });
      })
      .catch(handleApiError);
  };

  const handleGetReceiveNoteItemReplaced = async (articleId: string) => {
    if (isNilOrEmpty(props.receiveNoteId)) {
      return;
    }

    const request = {
      receiveNoteId: props.receiveNoteId as string,
      articleId,
      receiveNoteItemId: props.receiveNoteItemId as string,
    };

    await getReceiveNoteItemNew(request)
      .unwrap()
      .then((result) => {
        // When replacing an article, we want to:
        // 1. Update the article details display (ArticleDetailCard)
        // 2. Keep the existing receiveNoteItem and deliveryItem form values unchanged
        // 3. Update SupplierArticle form fields with new article's supplier mapping and reseed the selling price
        // This preserves user-entered quantities and purchase prices while updating article-specific data

        // Update ArticleDetailCard data, keeping the delivery item detail display untouched
        setReceiveNoteItemDetails((prevDetails) =>
          isNil(prevDetails) ? undefined : {...prevDetails, articleDetail: result.article}
        );

        // Update receiveNoteItemNew so manufacturerNumber and manufacturerId reflect the new article
        setReceiveNoteItemNew(result);

        // Only update SupplierArticle fields, keep receiveNoteItem and deliveryItem unchanged
        resetAfterArticleReplacement(result);

        showNotification.success(
          i18n.t('entity.warehouse.notification.articleReplacedSuccessfully')
        );
        setTimeout(
          () => refreshDmsDatagrid('warehouse-availability'),
          WAREHOUSE_AVAILABILITY_REFRESH_DELAY_MS
        );
      })
      .catch(handleApiError);
  };

  const handleCheckItem = async (config: HandleCheckItemConfig) => {
    setSelectedStorageLocationId(null);
    formApiRef.current?.setValue('article.storageLocationId', null);

    const articleIdResponse = await getArticleIdByManufacturerNumberAndManufacturerId({
      warehouseId: config.warehouseId,
      manufacturerNumber: config.catalogNumber,
      manufacturerId: config.manufacturerId,
      supplierId: config.supplierId,
    })
      .unwrap()
      .then((response) => {
        setLastCheckedState(config);
        return response;
      })
      .catch(handleApiError);

    if (!articleIdResponse?.articleId) {
      setArticleState('doesNotExist');
      setReceiveNoteItemNew(null);

      if (articleIdResponse?.name) {
        formApiRef.current?.setValue('article.name', articleIdResponse.name);
      }
      if (articleIdResponse?.measurementUnitId) {
        formApiRef.current?.setValue('article.handlingUnit', articleIdResponse.measurementUnitId);
      }
      return;
    }

    await handleGetReceiveNoteItem(articleIdResponse.articleId, false);
  };

  const handleArticleState = (
    receiveNoteItem: GetReceiveNoteItemNewResponse | Nullish,
    wasCreated: boolean
  ) => {
    if (isNil(receiveNoteItem)) {
      return setArticleState('doesNotExist');
    }

    if (wasCreated) {
      setArticleState('hasBeenCreated');
      handleMapPrevFormValues(receiveNoteItem);
      setReceiveNoteItemNew(receiveNoteItem);
      return;
    }

    setArticleState('doesExist');
    handleMapPrevFormValues(receiveNoteItem);
    setReceiveNoteItemNew(receiveNoteItem);
  };

  const handleRefreshArticle = async (formApi: UseFormReturn<ReceiveNoteItemFormType>) => {
    await getReceiveNoteItemRefreshed({
      receiveNoteId: props.receiveNoteId as string,
      articleId: articleId as string,
    })
      .unwrap()
      .then((receiveNoteItemRefreshed) => {
        mapWarehouseUnit(receiveNoteItemRefreshed.article.handlingUnit, formApi);
        setReceiveNoteItemDetails({
          deliveryItemDetail: {
            manufacturerNumber: receiveNoteItemRefreshed?.deliveryNoteItem?.manufacturerNumber,
            manufacturerId: receiveNoteItemRefreshed?.deliveryNoteItem?.manufacturerId,
            supplierNumber: receiveNoteItemRefreshed?.deliveryNoteItem?.supplierNumber,
            name: receiveNoteItemRefreshed?.deliveryNoteItem?.name,
            description: receiveNoteItemRefreshed?.deliveryNoteItem?.description,
          },
          articleDetail: receiveNoteItemRefreshed.article,
        });
      })
      .catch(handleApiError);
  };

  const onFormSubmitHandle: FormSubmitHandler<ReceiveNoteItemFormType> = async (formValues) => {
    if (articleState === 'doesNotExist') {
      if (!leafId) {
        setLeafIdErrorMessage(i18n.t('general.validations.fieldIsRequired'));
        return;
      }

      const requestData: PostArticleRequest = {
        warehouseId: formValues.article.warehouseId,
        isAclScoped: true,
        body: {
          manufacturerNumber: formValues.article.catalogNumber,
          manufacturerId: formValues.article.manufacturerId,
          name: formValues.article.name,
          storageLocationId: formValues.article.storageLocationId,
          handlingUnit: formValues.article.handlingUnit,
          dispensingUnit: formValues.article.dispensingUnit,
          treeFolder: {leafId},
          supplierId: formValues.article.supplierId, // used only for price list tab flow
        },
      };

      handleCreateNewArticle(requestData);

      return;
    }

    const supplierArticlePromptValidationRequestPayload: PutSupplierArticlePromptValidationRequest =
      {
        articleId: replaceArticleId ?? (articleId as string),
        supplierId: supplierId as string,
        manufacturerNumber: formValues.article.catalogNumber as string,
        supplierOrderingNumber: formValues.supplierArticle.supplierOrderingNumber,
        supplierToWarehouseMapping: {
          supplierQuantity: formValues.supplierArticle.supplierQuantity,
          supplierUnitId: formValues.supplierArticle.supplierUnitId,
          warehouseQuantity: formValues.supplierArticle.warehouseQuantity,
          supplierBulkPackageQuantity: formValues.supplierArticle.supplierBulkPackageQuantity,
        },
      };

    await putSupplierArticlePromptValidation(supplierArticlePromptValidationRequestPayload)
      .unwrap()
      .then((result) => {
        if (not(result.isChanged)) {
          return handleSubmit(formValues);
        }

        openDialog(
          <SupplierArticlePrompt
            onDontSave={() => handleSubmit(formValues, false)}
            onSave={() => handleSubmit(formValues, true)}
            data-testid={suffixTestId('supplierArticlePrompt', props)}
          />,
          {
            id: SUPPLIER_ARTICLE_PROMPT,
            size: 'small',
            'data-testid': suffixTestId('dialogs.supplierArticlePrompt', props),
          }
        );
      })
      .catch(handleApiError);
  };

  const handleSubmit = async (
    formValues: ReceiveNoteItemFormType,
    shouldUpdateMapping?: boolean
  ) => {
    if (props.context === 'BASKET') {
      return handleBasketSubmit(formValues, shouldUpdateMapping);
    }

    if (isCreating) {
      const requestBody: PostReceiveNoteBasketItemRequest['body'] = {
        receiveNoteId: props.receiveNoteId as string,
        articleId: articleId as string,
        quantity: formValues.receiveNoteItem.quantity,
        purchasePrice: formValues.receiveNoteItem.unitPrice,
        stockValue: formValues.receiveNoteItem.totalPrice,
        vatType: formValues.receiveNoteItem.vatType,
        salePriceWithoutVat: formValues.receiveNoteItem.isUnitSellingPriceEnabled
          ? formValues.receiveNoteItem.unitSellingPrice
          : undefined,
        supplierArticle: {
          supplierQuantity: formValues.supplierArticle.supplierQuantity,
          supplierUnitId: formValues.supplierArticle.supplierUnitId,
          warehouseQuantity: formValues.supplierArticle.warehouseQuantity,
          supplierOrderingNumber: formValues.supplierArticle.supplierOrderingNumber,
          supplierBulkPackageQuantity: formValues.supplierArticle.supplierBulkPackageQuantity,
          isUpdateMapping: shouldUpdateMapping ?? false,
        },
      };

      if (isNil(props.onAddToBasket)) {
        return logError('Missing onAddToBasket');
      }

      return await props.onAddToBasket(requestBody);
    }

    const receiveNoteItemRequest: PatchReceiveNoteItemManualRequest = {
      receiveNoteItemId: props.receiveNoteItemId as string,
      body: {
        quantity: formValues.receiveNoteItem.quantity,
        purchasePrice: formValues.receiveNoteItem.unitPrice,
        stockValue: formValues.receiveNoteItem.totalPrice,
        vatType: formValues.receiveNoteItem.vatType,
        salePriceWithoutVat: formValues.receiveNoteItem.isUnitSellingPriceEnabled
          ? formValues.receiveNoteItem.unitSellingPrice
          : undefined,
        deliveryItem: {
          quantityVerified: formValues.deliveryItem.quantityVerified,
          unitPrice: formValues.deliveryItem.purchasePriceVerified,
          stockValue: formValues.deliveryItem.stockValueVerified,
          vatType: formValues.deliveryItem.vatType,
        },
        supplierArticle: {
          supplierQuantity: formValues.supplierArticle.supplierQuantity,
          supplierUnitId: formValues.supplierArticle.supplierUnitId,
          warehouseQuantity: formValues.supplierArticle.warehouseQuantity,
          supplierOrderingNumber: formValues.supplierArticle.supplierOrderingNumber,
          supplierBulkPackageQuantity: formValues.supplierArticle.supplierBulkPackageQuantity,
          isUpdateMapping: shouldUpdateMapping ?? false,
        },
        replacedArticle: {
          articleId: replaceArticleId ?? (articleId as string),
        },
      },
    };

    await patchReceiveNoteItemManual(receiveNoteItemRequest)
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('page.warehouse.notification.receiveNoteItemUpdated'));
        refreshDmsDatagrid('receive-note-item');
        if (shouldCloseAfterSaveRef.current) {
          closeDialog(RECEIVE_NOTE_ITEM_DIALOG);
          props.onClose?.();
          return;
        }
        formApiRef.current?.reset(formValues);
      })
      .catch((error: ApiError) => {
        shouldCloseAfterSaveRef.current = true;
        handleApiError(error);
      });
  };

  const handleBasketSubmit = async (
    formValues: ReceiveNoteItemFormType,
    shouldUpdateMapping?: boolean
  ) => {
    const requestBody: PostReceiveNoteBasketItemRequest['body'] = {
      receiveNoteId: props.receiveNoteId as string,
      articleId: articleId as string,
      quantity: formValues.receiveNoteItem.quantity,
      purchasePrice: formValues.receiveNoteItem.unitPrice,
      stockValue: formValues.receiveNoteItem.totalPrice,
      vatType: formValues.receiveNoteItem.vatType,
      salePriceWithoutVat: formValues.receiveNoteItem.isUnitSellingPriceEnabled
        ? formValues.receiveNoteItem.unitSellingPrice
        : undefined,
      supplierArticle: {
        supplierQuantity: formValues.supplierArticle.supplierQuantity,
        supplierUnitId: formValues.supplierArticle.supplierUnitId,
        warehouseQuantity: formValues.supplierArticle.warehouseQuantity,
        supplierOrderingNumber: formValues.supplierArticle.supplierOrderingNumber,
        supplierBulkPackageQuantity: formValues.supplierArticle.supplierBulkPackageQuantity,
        isUpdateMapping: shouldUpdateMapping ?? false,
      },
    };

    if (isNil(props.onAddToBasket)) {
      return logError('Missing onAddToBasket');
    }

    await props.onAddToBasket(requestBody);
  };

  // eslint-disable-next-line complexity -- Pre-existing violation
  const handleMapPrevFormValues = (
    data: GetReceiveNoteItemResponse | GetReceiveNoteItemNewResponse | Nullish
  ) => {
    const mappedValues = {
      article: {
        name: data?.article.name,
        manufacturerId: data?.article.manufacturerId,
        catalogNumber: data?.article.manufacturerNumber,
        warehouseId: data?.article.warehouseId,
        storageLocationId: data?.article.storageLocationId,
        handlingUnit: data?.article.handlingUnit,
        dispensingUnit: data?.article.dispensingUnit,
        supplierId: data?.article.supplierId,
      },
      receiveNoteItem: {
        quantity: defaultTo(1, data?.receiveNoteItem?.quantity),
        unitPrice: defaultTo(0, data?.receiveNoteItem?.purchasePrice),
        totalPrice: defaultTo(0, data?.receiveNoteItem?.purchaseValue),
        vatType: defaultTo('S', data?.receiveNoteItem?.vatType),
        isUnitSellingPriceEnabled: false,
        unitSellingPrice: data?.article?.dispensingPrices?.saleBasePriceWithoutVat ?? undefined,
      },
      deliveryItem: {
        quantityVerified: defaultTo(1, data?.deliveryNoteItem?.quantityVerified),
        unitPrice: defaultTo(0, data?.deliveryNoteItem?.purchasePrice),
        totalPrice: defaultTo(0, data?.deliveryNoteItem?.purchaseValue),
        vatType: defaultTo('S', data?.deliveryNoteItem?.vatType),
        purchasePriceVerified: defaultTo(0, data?.deliveryNoteItem?.purchasePriceVerified),
        stockValueVerified: defaultTo(0, data?.deliveryNoteItem?.stockValueVerified),
      },
      supplierArticle: {
        supplierQuantity: defaultTo(1, data?.supplierArticle?.supplierQuantity),
        supplierUnitId: data?.supplierArticle?.supplierUnitId,
        warehouseQuantity: defaultTo(1, data?.supplierArticle?.warehouseQuantity),
        warehouseUnitId: data?.article?.handlingUnit,
        supplierOrderingNumber: data?.supplierArticle?.supplierOrderingNumber,
        supplierBulkPackageQuantity: defaultTo(
          1,
          data?.supplierArticle?.supplierBulkPackageQuantity
        ),
        supplier: getSupplierNameById(suppliers, data?.supplierArticle?.supplierId),
        supplierDiscountGroup: data?.supplierArticle?.supplierDiscountGroupCode,
      },
    };
    setPrevFormValues(mappedValues);
  };

  // eslint-disable-next-line complexity -- Pre-existing violation
  const resetFormValues = (data: GetReceiveNoteItemNewResponse | Nullish) => {
    formApiRef?.current?.reset({
      article: {
        name: data?.article.name,
        manufacturerId: data?.article.manufacturerId,
        catalogNumber: data?.article.manufacturerNumber,
        warehouseId: data?.article.warehouseId,
        storageLocationId: data?.article.storageLocationId,
        handlingUnit: data?.article.handlingUnit,
        dispensingUnit: data?.article.dispensingUnit,
        supplierId: data?.article.supplierId,
      },
      receiveNoteItem: {
        quantity: defaultTo(1, data?.receiveNoteItem?.quantity),
        unitPrice: defaultTo(0, data?.receiveNoteItem?.purchasePrice),
        totalPrice: defaultTo(0, data?.receiveNoteItem?.purchaseValue),
        vatType: defaultTo('S', data?.receiveNoteItem?.vatType),
        isUnitSellingPriceEnabled: false,
        unitSellingPrice: data?.article?.dispensingPrices?.saleBasePriceWithoutVat ?? undefined,
      },
      deliveryItem: {
        quantityVerified: defaultTo(1, data?.deliveryNoteItem?.quantityVerified),
        unitPrice: defaultTo(0, data?.deliveryNoteItem?.purchasePrice),
        totalPrice: defaultTo(0, data?.deliveryNoteItem?.purchaseValue),
        vatType: defaultTo('S', data?.deliveryNoteItem?.vatType),
        purchasePriceVerified: defaultTo(0, data?.deliveryNoteItem?.purchasePriceVerified),
        stockValueVerified: defaultTo(0, data?.deliveryNoteItem?.stockValueVerified),
      },
      supplierArticle: {
        supplierQuantity: data?.supplierArticle?.supplierQuantity,
        supplierUnitId: data?.supplierArticle?.supplierUnitId,
        warehouseQuantity: data?.supplierArticle?.warehouseQuantity,
        warehouseUnitId: data?.article?.handlingUnit,
        supplierOrderingNumber: data?.supplierArticle?.supplierOrderingNumber,
        supplierBulkPackageQuantity: data?.supplierArticle?.supplierBulkPackageQuantity,
        supplier: getSupplierNameById(suppliers, data?.supplierArticle?.supplierId),
        supplierDiscountGroup: data?.supplierArticle?.supplierDiscountGroupCode,
      },
    });
  };

  // eslint-disable-next-line complexity -- Pre-existing violation
  const resetAfterArticleReplacement = (data: GetReceiveNoteItemNewResponse | Nullish) => {
    const formApi = formApiRef?.current;

    if (isNil(formApi)) {
      return;
    }

    const currentValues = formApi.getValues();

    formApi.reset({
      ...currentValues,
      article: {
        ...currentValues.article,
        catalogNumber: data?.article?.manufacturerNumber,
        manufacturerId: data?.article?.manufacturerId,
        name: data?.article?.name,
        warehouseId: data?.article?.warehouseId,
        storageLocationId: data?.article?.storageLocationId,
        handlingUnit: data?.article?.handlingUnit,
        dispensingUnit: data?.article?.dispensingUnit,
      },
      receiveNoteItem: {
        ...currentValues.receiveNoteItem,
        // selling price belongs to the article, so it must not carry over from the replaced one
        unitSellingPrice: data?.article?.dispensingPrices?.saleBasePriceWithoutVat ?? undefined,
      },
      supplierArticle: {
        ...currentValues.supplierArticle,
        supplierQuantity: data?.supplierArticle?.supplierQuantity,
        supplierUnitId: data?.supplierArticle?.supplierUnitId,
        warehouseQuantity: data?.supplierArticle?.warehouseQuantity,
        warehouseUnitId: data?.article?.handlingUnit,
        supplierOrderingNumber: data?.supplierArticle?.supplierOrderingNumber,
        supplierBulkPackageQuantity: data?.supplierArticle?.supplierBulkPackageQuantity,
        supplier: getSupplierNameById(suppliers, data?.supplierArticle?.supplierId),
        supplierDiscountGroup: data?.supplierArticle?.supplierDiscountGroupCode,
      },
    });
  };

  const handleChangeCategory = (activeFolderId: string | undefined) => {
    setLeafIdErrorMessage(undefined);
    setLeafId(activeFolderId);
  };

  const handleCreateNewArticle = (data: PostArticleRequest) => {
    createArticle(data)
      .unwrap()
      .then((postResult) => {
        handleGetReceiveNoteItem(postResult.articleId, true);
      })
      .catch(handleWarehouseAclApiError);
  };

  const handleReplaceArticle = () => {
    const receiveNoteWarehouseId =
      receiveNoteItem?.article?.warehouseId ?? receiveNoteItemNew?.article?.warehouseId;
    const receiveNoteWarehouse =
      props.warehouse ?? warehouses?.find((warehouse) => warehouse.id === receiveNoteWarehouseId);

    const handleArticleReplaced = (articleId: string) => {
      setReplaceArticleId(articleId);
      closeDialog(REPLACE_ARTICLE_DIALOG);
    };

    const handleCreateArticleFromReplaceModal = (defaultValues: ArticleCreateDefaultValues) => {
      const REPLACE_ARTICLE_CREATE_ARTICLE_DIALOG = 'replace-article-create-article';

      const handleOnArticleCreate = (createdArticleData: CreatedArticleData) => {
        handleArticleReplaced(createdArticleData.articleId);
        closeDialog(REPLACE_ARTICLE_CREATE_ARTICLE_DIALOG);
      };

      openDialog(
        <ArticleCreate
          defaultValues={defaultValues}
          onArticleCreate={handleOnArticleCreate}
          onClose={() => closeDialog(REPLACE_ARTICLE_CREATE_ARTICLE_DIALOG)}
          isWarehouseDisabled
          hasCreateAndReplaceButton
          submitButtonTitle={i18n.t('entity.warehouse.labels.createAndReplaceArticle')}
          data-testid={suffixTestId('forms.createArticle', props)}
        />,
        {
          id: REPLACE_ARTICLE_CREATE_ARTICLE_DIALOG,
          title: i18n.t('entity.warehouse.labels.createNewArticle'),
          scrollBehavior: 'outside',
          withAdditionalFooter: true,
          'data-testid': suffixTestId('dialogs.createArticle', props),
        }
      );
    };

    openDialog(
      <ReplaceArticleModal
        authorizationProfileId={props.authorizationProfileId}
        tenant={props.tenant}
        warehouse={receiveNoteWarehouse}
        receiveNoteId={props.receiveNoteId}
        onArticleReplaced={handleArticleReplaced}
        onCreateArticleFromPricelist={handleCreateArticleFromReplaceModal}
        onCreateArticle={handleCreateArticleFromReplaceModal}
        data-testid={suffixTestId('replaceArticleModal', props)}
      />,
      {
        id: REPLACE_ARTICLE_DIALOG,
        'data-testid': suffixTestId('dialogs.replaceArticle', props),
      }
    );
  };

  const mapWarehouseUnit = (
    handlingUnit: string,
    formApi: UseFormReturn<ReceiveNoteItemFormType>
  ) => {
    formApi.setValue('supplierArticle.warehouseUnitId', handlingUnit);
  };

  const isLoading =
    isReceiveNoteLoading ||
    isReceiveNoteItemLoading ||
    areSuppliersLoading ||
    isWarehouseLoading ||
    isDefaultTreeFolderLoading ||
    isLoadingHandlingUnits ||
    isDispensingUnitPresetsLoading;

  const segments = [
    {
      value: Segments.AVAILABILITY,
      label: i18n.t('entity.warehouse.labels.availability'),
      content: (
        <AvailabilitySegment
          articleId={replaceArticleId ?? articleId}
          manufacturerNumber={manufacturerNumber}
          manufacturerId={manufacturerId}
          data-testid={suffixTestId('segments.availability', props)}
        />
      ),
    },
    {
      value: Segments.REQUESTS,
      label: i18n.t('entity.warehouse.labels.requests'),
      content: (
        <RequestsSegment
          receiveNoteItemId={props.receiveNoteItemId}
          data-testid={suffixTestId('segments.requests', props)}
        />
      ),
    },
    {
      value: Segments.SUPPLIER_ORDERS,
      label: i18n.t('entity.warehouse.labels.supplierOrders'),
      content: (
        <SupplierOrdersSegment
          receiveNoteItemId={props.receiveNoteItemId}
          data-testid={suffixTestId('segments.supplierOrders', props)}
        />
      ),
    },
  ];

  const segmentContent = segments.find((segment) => segment.value === activeSegment)?.content;

  return (
    <DataStatus isLoading={isLoading} minHeight={100}>
      <Form<ReceiveNoteItemFormType>
        // We fetch item detail asynchronously in edit mode, therefore we need the key so the Form remounts properly after getting data.
        // If not editing (adding/creating), the form should not remount to properly react to resetFormValues calls.
        key={isEditing ? generateHashFromObjects(defaultValues) : undefined}
        experimentalZodSchema={ReceiveNoteItemDetailSchema(articleState)}
        defaultValues={defaultValues}
        onSubmit={onFormSubmitHandle}
        isDisabled={not(canEditReceiveNote)}
      >
        {/* eslint-disable-next-line complexity, max-lines-per-function -- Pre-existing violation */}
        {(control, formApi) => {
          // We need to have a ref of formApi to be able to check for item on mount
          formApiRef.current = formApi;
          // For controlling recalculations of prices
          formApi.watch((data, test) => handleChange(data, test, formApi));

          const manufacturerId = formApi.watch('article.manufacturerId');
          const catalogNumber = formApi.watch('article.catalogNumber');
          const warehouseId = formApi.watch('article.warehouseId');

          const isSearchInfoFulfill =
            isNilOrEmpty(catalogNumber) ||
            isNilOrEmpty(warehouseId) ||
            isNilOrEmpty(manufacturerId);

          const isCurrentStateChecked =
            isNotNil(lastCheckedState) &&
            manufacturerId === lastCheckedState?.manufacturerId &&
            catalogNumber === lastCheckedState?.catalogNumber &&
            warehouseId === lastCheckedState?.warehouseId;

          const isCheckItemButtonDisabled = isSearchInfoFulfill || isCurrentStateChecked;

          const isCurrentStateInvalid = isEditing ? false : !isCurrentStateChecked;

          const isSubmitDisabled =
            props.isAddingToBasket ||
            doesNotExist ||
            isPatchReceiveNoteItemLoading ||
            isEditingDisabled ||
            isOpeningBalanceEditingDisabled ||
            isTransferEditingDisabled ||
            isCurrentStateInvalid;

          return (
            <VStack>
              <VStack spacing={4}>
                <Show when={not(isEditing) && doesExist}>
                  <Alert
                    type="inline"
                    variant="info"
                    title={i18n.t('entity.warehouse.labels.itemIsAlreadyInDatabase')}
                    message={i18n.t('entity.warehouse.labels.itemDetailsPrefilled')}
                    data-testid={suffixTestId('alerts.itemInDatabase', props)}
                  />
                </Show>

                <Show when={not(isEditing) && doesNotExist}>
                  <Alert
                    type="inline"
                    variant="warning"
                    title={i18n.t('entity.warehouse.labels.itemNotInDatabase')}
                    message={i18n.t('entity.warehouse.labels.youCanCreateNewWarehouseArticle')}
                    data-testid={suffixTestId('alerts.itemNotInDatabase', props)}
                  />
                </Show>

                <Show when={not(isEditing) && hasBeenCreated}>
                  <Alert
                    type="inline"
                    variant="success"
                    title={i18n.t('entity.warehouse.labels.warehouseArticleWasSuccessfullyCreated')}
                    data-testid={suffixTestId('alerts.articleCreated', props)}
                  />
                </Show>

                <Grid columns={8} spacing={4}>
                  <GridItem span={1}>
                    <FormField
                      isDisabled
                      control={control}
                      name="receiveNoteItem.serialNumber"
                      type="number"
                      label={i18n.t('entity.warehouse.labels.sNo')}
                      data-testid={suffixTestId('inputs.serialNumber', props)}
                    />
                  </GridItem>
                  <GridItem span={1}>
                    <FormField
                      isDisabled
                      control={control}
                      name="deliveryItem.serialNumber"
                      type="number"
                      label={i18n.t('entity.warehouse.labels.sNoDeliveryNote')}
                      data-testid={suffixTestId('inputs.serialNumberDeliveryNote', props)}
                    />
                  </GridItem>
                  <GridItem span={2}>
                    <Tooltip
                      label={
                        isEditing
                          ? i18n.t('entity.warehouse.tooltips.catalogNumberDisabledDuringEditing')
                          : i18n.t('entity.warehouse.tooltips.catalogNumberDisabledWhileChecking')
                      }
                      isDisabled={!isCheckingForArticle && !isEditing}
                    >
                      <FormField
                        control={control}
                        name="article.catalogNumber"
                        type="text"
                        label={i18n.t('entity.warehouse.labels.catalogueNumber')}
                        isRequired
                        isDisabled={isCheckingForArticle || isEditing}
                        data-testid={suffixTestId('inputs.catalogNumber', props)}
                      />
                    </Tooltip>
                  </GridItem>
                  <GridItem span={2}>
                    <Tooltip
                      label={
                        isEditing
                          ? i18n.t('entity.warehouse.tooltips.manufacturerDisabledDuringEditing')
                          : i18n.t('entity.warehouse.tooltips.manufacturerDisabledWhileChecking')
                      }
                      isDisabled={!isCheckingForArticle && !isEditing}
                    >
                      <FormField
                        control={control}
                        name="article.manufacturerId"
                        type="choice"
                        label={i18n.t('entity.warehouse.labels.manufacturer')}
                        options={getOptionsFromManufacturers(manufacturers)}
                        isLoading={areManufacturersLoading}
                        isRequired
                        isDisabled={isCheckingForArticle || isEditing}
                        isNotClearable
                        menuInPortal
                        data-testid={suffixTestId('inputs.manufacturer', props)}
                      />
                    </Tooltip>
                  </GridItem>
                  <GridItem span={2}>
                    <Tooltip label={i18n.t('entity.warehouse.tooltips.disabled')}>
                      <FormField
                        isNotClearable
                        control={control}
                        name="article.warehouseId"
                        type="choice"
                        label={i18n.t('entity.warehouse.labels.warehouse')}
                        options={getOptionsFromWarehouses(warehouses)}
                        isRequired={isCreating}
                        isDisabled={true}
                        menuInPortal
                        data-testid={suffixTestId('inputs.warehouse', props)}
                      />
                    </Tooltip>
                  </GridItem>

                  <Hide when={isEditing}>
                    <GridItem span={3}>
                      <HStack spacing={2}>
                        <Tooltip
                          label={i18n.t('entity.warehouse.labels.currentStateIsChecked')}
                          isDisabled={!isCurrentStateChecked}
                        >
                          <Button
                            variant="secondary"
                            title={i18n.t('general.labels.checkItem')}
                            onClick={() =>
                              handleCheckItem({
                                manufacturerId,
                                catalogNumber,
                                warehouseId,
                                supplierId: props.receiveNoteSupplierId ?? undefined,
                              })
                            }
                            isLoading={isCheckingForArticle}
                            isDisabled={isCheckItemButtonDisabled}
                            data-testid={suffixTestId('actions.checkItem', props)}
                          />
                        </Tooltip>
                        <Show when={doesNotExist}>
                          <Tooltip
                            label={i18n.t('entity.warehouse.labels.firstlyCheckItem')}
                            isDisabled={isCurrentStateChecked}
                          >
                            <Button
                              type="submit"
                              variant="primary"
                              title={i18n.t('entity.warehouse.actions.createNewArticle')}
                              isLoading={isCreateArticleLoading}
                              isDisabled={!isCurrentStateChecked || isCalculateLoading}
                              data-testid={suffixTestId('actions.createNewArticle', props)}
                            />
                          </Tooltip>
                        </Show>
                      </HStack>
                    </GridItem>
                  </Hide>
                </Grid>
              </VStack>

              <Separator />

              <DataStatus isLoading={isLoadingMainContent} minHeight={100}>
                <Hide when={doesNotExist}>
                  <ReceiveNoteItemForm
                    control={control}
                    handlingUnit={articleHandlingUnitName ?? defaultHandlingUnit?.name}
                    currency={currency}
                    isChecked={isChecked}
                    isEditingDisabled={isEditingDisabled}
                    isOpeningBalanceEditingDisabled={isOpeningBalanceEditingDisabled}
                    isTransferEditingDisabled={isTransferEditingDisabled}
                    isFromReceiveNote={props.isFromReceiveNote}
                    data-testid={suffixTestId('sections.receiveNoteItem', props)}
                  />
                  <Separator />
                  <DeliveryItemForm
                    control={control}
                    deliveryNoteQuantity={deliveryNoteQuantity}
                    deliveryNoteUnitPrice={deliveryNoteUnitPrice}
                    deliveryNoteTotalPrice={deliveryNoteTotalPrice}
                    handlingUnit={articleHandlingUnitName ?? defaultHandlingUnit?.name}
                    currency={currency}
                    isChecked={isChecked}
                    isEditingDisabled={isEditingDisabled}
                    isOpeningBalanceEditingDisabled={isOpeningBalanceEditingDisabled}
                    isTransferEditingDisabled={isTransferEditingDisabled}
                    isFromReceiveNote={props.isFromReceiveNote}
                    receiveNoteState={props.state}
                    receiveNoteType={props.receiveNoteType}
                    data-testid={suffixTestId('sections.deliveryItem', props)}
                  />
                  <Separator />
                  <SupplierArticleForm
                    control={control}
                    handlingUnits={handlingUnits}
                    isChecked={isChecked}
                    isEditingDisabled={isEditingDisabled}
                    isOpeningBalanceEditingDisabled={isOpeningBalanceEditingDisabled}
                    isTransferEditingDisabled={isTransferEditingDisabled}
                    supplierDiscountGroups={supplierDiscountGroups}
                    loadingState={{
                      isLoadingHandlingUnits,
                      isLoadingSupplierDiscountGroups,
                    }}
                    data-testid={suffixTestId('sections.supplierArticle', props)}
                  />
                  <Space vertical={4} />
                  <DeliveryItemDetailCard
                    deliveryItemDetail={receiveNoteItemDetails?.deliveryItemDetail}
                    deliveryNoteQuantity={deliveryNoteQuantity}
                    handlingUnit={articleHandlingUnitName ?? defaultHandlingUnit?.name}
                    isDisabled={isNotChecked}
                    isExpanded={shouldCardsBeExpanded}
                    isRefreshingDetails={isRefreshingDetails}
                    data-testid={suffixTestId('sections.deliveryItemDetailCard', props)}
                  />
                  <Space vertical={4} />
                  <ArticleDetailCard
                    articleDetail={receiveNoteItemDetails?.articleDetail}
                    currency={currency}
                    isDisabled={isNotChecked}
                    isExpanded={shouldCardsBeExpanded}
                    isRefreshingDetails={isRefreshingDetails}
                    onRefresh={() => handleRefreshArticle(formApi)}
                    onReplaceArticle={() => handleReplaceArticle()}
                    warehouseName={warehouseName}
                    isArticleReplacement={isArticleReplacement}
                    data-testid={suffixTestId('sections.articleDetailCard', props)}
                  />
                  <Space vertical={4} />
                </Hide>

                <Show when={isEditing}>
                  <HStack>
                    <Segment<Segments>
                      value={activeSegment}
                      onChange={setActiveSegment}
                      options={segments}
                    />
                  </HStack>
                  <Box>{segmentContent}</Box>
                </Show>

                <DialogFooter>
                  <HStack>
                    <Show when={isArticleReplaced || isNotNil(replaceArticleId)}>
                      <HStack align="center">
                        <Flag
                          isSubtle
                          colorScheme="yellow"
                          label={i18n.t('entity.warehouse.labels.replaceArticle')}
                        />
                        <Space horizontal={2} />
                        <Label tooltip={i18n.t('general.labels.replacedArticle')}>
                          {i18n.t('entity.warehouse.labels.receiveItemHasBeenReplacedManually')}
                        </Label>
                      </HStack>
                    </Show>
                    <Space fillAvailable />
                    <ButtonGroup align="right">
                      <Button
                        variant="secondary"
                        title={i18n.t('general.labels.discard')}
                        onClick={() => {
                          closeCurrentDialog();
                          props.onClose?.();
                        }}
                        data-testid={suffixTestId('actions.discard', props)}
                      />
                      {isEditing ? (
                        <>
                          <FormButton
                            isDisabled={isSubmitDisabled || isCalculateLoading}
                            control={control}
                            type="submit"
                            variant="secondary"
                            title={i18n.t('general.actions.save')}
                            onClick={() => {
                              shouldCloseAfterSaveRef.current = false;
                            }}
                            data-testid={suffixTestId('actions.save', props)}
                          />
                          <FormButton
                            isDisabled={isSubmitDisabled || isCalculateLoading}
                            control={control}
                            type="submit"
                            variant="primary"
                            title={i18n.t('general.actions.saveAndClose')}
                            onClick={() => {
                              shouldCloseAfterSaveRef.current = true;
                            }}
                            data-testid={suffixTestId('actions.saveAndClose', props)}
                          />
                        </>
                      ) : (
                        <FormButton
                          isDisabled={isSubmitDisabled || isCalculateLoading}
                          control={control}
                          type="submit"
                          variant="primary"
                          title={i18n.t('general.labels.add')}
                          data-testid={suffixTestId('actions.submit', props)}
                        />
                      )}
                    </ButtonGroup>
                  </HStack>
                </DialogFooter>

                <Show when={doesNotExist}>
                  <VStack spacing={4} data-testid={props['data-testid']}>
                    <Card
                      variant="inlineGrey"
                      title={i18n.t('entity.warehouse.labels.articleDetail')}
                    >
                      <Grid columns={4}>
                        <GridItem span={2}>
                          <FormField
                            type="text"
                            name="article.catalogNumber"
                            label={i18n.t('entity.warehouse.labels.catalogueNumber')}
                            control={control}
                            isRequired
                            data-testid={suffixTestId('inputs.catalogNumber', props)}
                          />
                        </GridItem>
                        <GridItem span={2}>
                          <FormField
                            type="choice"
                            name="article.manufacturerId"
                            label={i18n.t('entity.warehouse.labels.manufacturer')}
                            options={getOptionsFromManufacturers(manufacturers)}
                            control={control}
                            isRequired
                            isNotClearable
                            menuInPortal
                            data-testid={suffixTestId('inputs.manufacturer', props)}
                          />
                        </GridItem>
                        <GridItem span={2}>
                          <FormField
                            type="text"
                            name="article.name"
                            label={i18n.t('entity.warehouse.labels.name')}
                            control={control}
                            isRequired
                            data-testid={suffixTestId('inputs.name', props)}
                          />
                        </GridItem>
                        <GridItem span={2}>
                          {/* Warehouse account field will go here in the future */}
                          <div />
                        </GridItem>
                        <GridItem span={2}>
                          <TextInput
                            isDisabled
                            value={storageLocationName}
                            label={i18n.t('entity.warehouse.labels.storageLocation')}
                            labelActions={[
                              {
                                title: i18n.t('entity.warehouse.labels.assignStorageLocation'),
                                leftIcon: 'content/link',
                                isDisabled: isNilOrEmpty(warehouseId),
                                onClick: () => {
                                  openDialog(
                                    <AssignLocationDialog
                                      warehouseId={warehouseId}
                                      dialogId={ASSIGN_LOCATION_DIALOG_ID}
                                      onAssign={(id) => {
                                        formApi.setValue('article.storageLocationId', id, {
                                          shouldDirty: true,
                                        });
                                        setSelectedStorageLocationId(id);
                                      }}
                                    />,
                                    {
                                      title: i18n.t(
                                        'entity.warehouse.labels.assignStorageLocation'
                                      ),
                                      id: ASSIGN_LOCATION_DIALOG_ID,
                                      size: 'large',
                                    }
                                  );
                                },
                              },
                            ]}
                            data-testid={suffixTestId('inputs.storageLocation', props)}
                          />
                        </GridItem>
                        <GridItem span={1}>
                          <FormField
                            isRequired
                            isNotClearable
                            isLoading={isLoadingHandlingUnits}
                            type="choice"
                            name="article.handlingUnit"
                            label={i18n.t('entity.warehouse.labels.unit')}
                            options={getOptionsFromHandlingUnits(handlingUnits)}
                            control={control}
                            menuInPortal
                            data-testid={suffixTestId('inputs.unit', props)}
                          />
                        </GridItem>
                        <GridItem span={1}>
                          <FormField
                            control={control}
                            name="article.dispensingUnit"
                            type="number"
                            label={i18n.t('entity.warehouse.labels.dispensingUnit')}
                            labelActions={
                              dispensingUnitPresets
                                ? [
                                    {
                                      title: i18n.t('entity.warehouse.labels.presets'),
                                      onClick: (e) =>
                                        openDropdown(
                                          e,
                                          dispensingUnitPresets?.map((preset) => (
                                            <DropdownItem
                                              key={preset.id}
                                              label={preset.value.toString()}
                                              onClick={() =>
                                                formApi.setValue(
                                                  'article.dispensingUnit',
                                                  preset.value
                                                )
                                              }
                                            />
                                          ))
                                        ),
                                    },
                                  ]
                                : undefined
                            }
                            decimalPlaces={4}
                            data-testid={suffixTestId('dispensingUnit', props)}
                          />
                        </GridItem>
                        <GridItem span={4}>
                          <ArticleCreateTreeFolderPath
                            leafId={leafId}
                            onChange={(activeFolder) => handleChangeCategory(activeFolder?.id)}
                            invalidMessage={leafIdErrorMessage}
                            isRequired
                            data-testid={suffixTestId('inputs.category', props)}
                          />
                        </GridItem>
                      </Grid>
                    </Card>
                  </VStack>
                </Show>
              </DataStatus>
            </VStack>
          );
        }}
      </Form>
    </DataStatus>
  );
}
